/**
 * Usuários e acessos.
 *
 * Uma tela só para pessoa e permissão, e não duas. Quem administra acesso está
 * quase sempre respondendo a uma pergunta que mistura as duas coisas — "a
 * Cláudia ainda entra?", "quem tem acesso a esta escola?", "por que ela não
 * consegue aprovar?" — e separar em "cadastro" e "permissões" obriga a
 * atravessar a interface para responder qualquer uma delas.
 *
 * A lista de papéis oferecidos vem do servidor (`/usuarios/papeis-disponiveis`),
 * não de uma constante daqui: é a mesma regra que a API aplica ao gravar, então
 * a tela nunca oferece um papel que resultaria em 403.
 */

import { PAPEIS, type Papel } from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, type Item, type Lista } from '../api/cliente'
import { Avatar } from '../componentes/Avatar'
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Modal,
  Selecao,
  Tabela,
  Vazio,
  formatarData,
} from '../componentes/ui'
import { useAuth } from '../estado/auth'

const ROTULO_PAPEL: Record<Papel, string> = {
  admin: 'Administrador técnico',
  admin_master_egba: 'Administrador master EGBA',
  egba: 'Equipe EGBA',
  parceiro: 'Parceiro',
  cliente: 'Cliente (titular)',
  operador: 'Operador',
  leitura: 'Leitura / auditoria',
}

const TOM_PAPEL: Record<Papel, 'neutro' | 'marca' | 'sucesso' | 'atencao' | 'perigo'> = {
  admin: 'perigo',
  admin_master_egba: 'perigo',
  egba: 'marca',
  parceiro: 'atencao',
  cliente: 'sucesso',
  operador: 'neutro',
  leitura: 'neutro',
}

interface PapelAtribuido {
  id: string
  papel: Papel
  organizacaoId: string | null
  organizacaoNome: string | null
  criadoEm: string
}

interface UsuarioLinha {
  id: string
  nome: string
  email: string
  telefone: string | null
  cargo: string | null
  setor: string | null
  temFoto: boolean
  ativo: boolean
  mfaAtivo: boolean
  ultimoLoginEm: string | null
  criadoEm: string
  papeis: PapelAtribuido[]
  /** Verticais visíveis no painel: escolas, companhias ou ambas. */
  verticais?: Array<'escolas' | 'companhias'>
}

interface SessaoAtiva {
  id: string
  ip: string | null
  userAgent: string | null
  criadoEm: string
  expiraEm: string
}

interface EventoHistorico {
  id: string
  acao: string
  entidade: string
  entidadeId: string | null
  ip: string | null
  criadoEm: string
  souOAtor: boolean
}

interface UsuarioDetalhe extends UsuarioLinha {
  sessoesAtivas: SessaoAtiva[]
  historico: EventoHistorico[]
}

interface Organizacao {
  id: string
  razaoSocial: string
  nomeFantasia: string | null
}

function nomeOrg(o: Organizacao): string {
  return o.nomeFantasia?.trim() || o.razaoSocial
}

/** Resume o user-agent: a string inteira é ilegível e ocupa a linha toda. */
function dispositivo(ua: string | null): string {
  if (!ua) return 'Desconhecido'
  const so = /Windows/i.test(ua)
    ? 'Windows'
    : /Macintosh|Mac OS/i.test(ua)
      ? 'macOS'
      : /Android/i.test(ua)
        ? 'Android'
        : /iPhone|iPad/i.test(ua)
          ? 'iOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : 'Outro'
  const nav = /Edg\//i.test(ua)
    ? 'Edge'
    : /Chrome\//i.test(ua)
      ? 'Chrome'
      : /Safari\//i.test(ua)
        ? 'Safari'
        : /Firefox\//i.test(ua)
          ? 'Firefox'
          : 'Navegador'
  return `${nav} · ${so}`
}

export function Usuarios() {
  const qc = useQueryClient()
  const { usuario: eu } = useAuth()

  const [busca, setBusca] = useState('')
  const [filtroPapel, setFiltroPapel] = useState('')
  const [filtroAtivo, setFiltroAtivo] = useState<'ativos' | 'inativos' | 'todos'>('ativos')
  const [aberto, setAberto] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [senhaGerada, setSenhaGerada] = useState<{ nome: string; senha: string } | null>(null)

  const params = new URLSearchParams({ ativo: filtroAtivo })
  if (busca.trim()) params.set('busca', busca.trim())
  if (filtroPapel) params.set('papel', filtroPapel)

  const { data, isLoading } = useQuery({
    queryKey: ['usuarios', busca, filtroPapel, filtroAtivo],
    queryFn: () => api.get<Lista<UsuarioLinha>>(`/api/v1/usuarios?${params}`),
  })

  const { data: concedeveis } = useQuery({
    queryKey: ['papeis-disponiveis'],
    queryFn: () => api.get<{ dados: Papel[] }>('/api/v1/usuarios/papeis-disponiveis'),
  })

  const { data: orgs } = useQuery({
    queryKey: ['organizacoes-lista'],
    queryFn: () => api.get<Lista<Organizacao>>('/api/v1/organizacoes?porPagina=100'),
  })

  function recarregar(mensagem?: string) {
    void qc.invalidateQueries({ queryKey: ['usuarios'] })
    if (aberto) void qc.invalidateQueries({ queryKey: ['usuario', aberto] })
    setErro(null)
    if (mensagem) setAviso(mensagem)
  }

  const lista = data?.dados ?? []
  const podeConceder = concedeveis?.dados ?? []

  if (isLoading) return <Carregando />

  return (
    <div className="space-y-5">
      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {aviso && !erro && <Aviso tom="sucesso">{aviso}</Aviso>}

      <Cartao
        titulo="Usuários e acessos"
        descricao="Quem entra, com que papel, em qual organização — e desde quando."
        acoes={
          podeConceder.length > 0 ? (
            <Botao tamanho="sm" onClick={() => setCriando(true)}>
              Novo usuário
            </Botao>
          ) : undefined
        }
      >
        <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_200px_170px]">
          <Campo
            rotulo="Buscar"
            placeholder="Nome ou e-mail"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Selecao
            rotulo="Papel"
            value={filtroPapel}
            onChange={(e) => setFiltroPapel(e.target.value)}
            opcoes={[
              { valor: '', texto: 'Todos os papéis' },
              ...PAPEIS.map((p) => ({ valor: p, texto: ROTULO_PAPEL[p] })),
            ]}
          />
          <Selecao
            rotulo="Situação"
            value={filtroAtivo}
            onChange={(e) => setFiltroAtivo(e.target.value as typeof filtroAtivo)}
            opcoes={[
              { valor: 'ativos', texto: 'Ativos' },
              { valor: 'inativos', texto: 'Desativados' },
              { valor: 'todos', texto: 'Todos' },
            ]}
          />
        </div>

        {lista.length === 0 ? (
          <Vazio
            titulo="Nenhum usuário encontrado"
            descricao="Ajuste a busca ou os filtros acima."
          />
        ) : (
          <Tabela>
            <thead>
              <tr className="border-b border-[color:var(--color-borda-suave)] text-left text-xs tracking-wider text-slate-400 uppercase">
                <th className="px-3 py-2 font-semibold">Pessoa</th>
                <th className="px-3 py-2 font-semibold">Papéis</th>
                <th className="px-3 py-2 font-semibold">MFA</th>
                <th className="px-3 py-2 font-semibold">Último acesso</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lista.map((u) => (
                <tr key={u.id} className={u.ativo ? '' : 'opacity-55'}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar usuarioId={u.id} nome={u.nome} temFoto={u.temFoto} />
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium text-slate-900">
                          <span className="truncate">{u.nome}</span>
                          {u.id === eu?.id && <Etiqueta tom="marca">você</Etiqueta>}
                          {!u.ativo && <Etiqueta tom="neutro">desativado</Etiqueta>}
                        </p>
                        <p className="truncate text-xs text-slate-500">{u.email}</p>
                        {(u.cargo || u.setor) && (
                          <p className="truncate text-xs text-slate-400">
                            {[u.cargo, u.setor].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.papeis.length === 0 && (
                        <Etiqueta tom="atencao">sem papel — não entra</Etiqueta>
                      )}
                      {u.papeis.map((p) => (
                        <Etiqueta key={p.id} tom={TOM_PAPEL[p.papel]}>
                          {ROTULO_PAPEL[p.papel]}
                          {p.organizacaoNome ? ` · ${p.organizacaoNome}` : ''}
                        </Etiqueta>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {u.mfaAtivo ? (
                      <Etiqueta tom="sucesso">ativo</Etiqueta>
                    ) : (
                      <Etiqueta tom="atencao">pendente</Etiqueta>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-500">
                    {u.ultimoLoginEm ? formatarData(u.ultimoLoginEm) : 'nunca entrou'}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Botao variante="secundario" tamanho="sm" onClick={() => setAberto(u.id)}>
                      Gerenciar
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </Cartao>

      {aberto && (
        <FichaUsuario
          id={aberto}
          aoFechar={() => setAberto(null)}
          podeConceder={podeConceder}
          organizacoes={orgs?.dados ?? []}
          meuId={eu?.id ?? ''}
          aoErro={setErro}
          aoSucesso={recarregar}
          aoGerarSenha={setSenhaGerada}
        />
      )}

      <NovoUsuario
        aberto={criando}
        aoFechar={() => setCriando(false)}
        podeConceder={podeConceder}
        organizacoes={orgs?.dados ?? []}
        aoErro={setErro}
        aoCriar={(nome, senha) => {
          setCriando(false)
          setSenhaGerada({ nome, senha })
          recarregar()
        }}
      />

      {/* A senha provisória sai uma vez só — por isso o aviso é explícito e o
          fechamento é deliberado, e não um clique fora do diálogo. */}
      <Modal
        aberto={Boolean(senhaGerada)}
        aoFechar={() => setSenhaGerada(null)}
        titulo="Senha provisória"
        descricao="Anote agora. Ela não será exibida de novo."
      >
        <p className="text-sm text-slate-600">
          Senha de acesso de <strong>{senhaGerada?.nome}</strong>:
        </p>
        <p className="mt-3 rounded-lg bg-slate-900 px-4 py-3 text-center font-mono text-lg tracking-wide text-white select-all">
          {senhaGerada?.senha}
        </p>
        <p className="mt-3 text-xs text-slate-500">
          Formada por palavras de propósito: é ditada ao telefone sem virar outra coisa. Peça a
          troca no primeiro acesso.
        </p>
        <div className="mt-5 flex justify-end">
          <Botao onClick={() => setSenhaGerada(null)}>Anotei</Botao>
        </div>
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------

function NovoUsuario({
  aberto,
  aoFechar,
  podeConceder,
  organizacoes,
  aoErro,
  aoCriar,
}: {
  aberto: boolean
  aoFechar: () => void
  podeConceder: Papel[]
  organizacoes: Organizacao[]
  aoErro: (e: string) => void
  aoCriar: (nome: string, senha: string) => void
}) {
  const [f, setF] = useState({
    nome: '',
    email: '',
    telefone: '',
    cargo: '',
    setor: '',
    papel: podeConceder[0] ?? ('operador' as Papel),
    organizacaoId: '',
  })

  const globais: Papel[] = ['admin', 'admin_master_egba', 'egba', 'leitura']
  const precisaOrg = !globais.includes(f.papel)

  const criar = useMutation({
    mutationFn: () =>
      api.post<Item<{ id: string; senhaProvisoria: string }>>('/api/v1/usuarios', {
        nome: f.nome,
        email: f.email,
        telefone: f.telefone || undefined,
        cargo: f.cargo || undefined,
        setor: f.setor || undefined,
        papel: f.papel,
        organizacaoId: precisaOrg ? f.organizacaoId : null,
      }),
    onSuccess: (r) => {
      aoCriar(f.nome, r.dados.senhaProvisoria)
      setF({ ...f, nome: '', email: '', telefone: '', cargo: '', setor: '' })
    },
    onError: (e: Error) => aoErro(e.message),
  })

  const pronto = f.nome.trim().length >= 3 && /.+@.+\..+/.test(f.email) && (!precisaOrg || f.organizacaoId)

  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo="Novo usuário" largura="lg">
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Nome completo" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} />
          <Campo
            rotulo="E-mail"
            type="email"
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
            ajuda="É com ele que a pessoa entra."
          />
          <Campo rotulo="Cargo" value={f.cargo} onChange={(e) => setF({ ...f, cargo: e.target.value })} />
          <Campo rotulo="Setor" value={f.setor} onChange={(e) => setF({ ...f, setor: e.target.value })} />
          <Campo rotulo="Telefone" value={f.telefone} onChange={(e) => setF({ ...f, telefone: e.target.value })} />
          <Selecao
            rotulo="Papel"
            value={f.papel}
            onChange={(e) => setF({ ...f, papel: e.target.value as Papel })}
            opcoes={podeConceder.map((p) => ({ valor: p, texto: ROTULO_PAPEL[p] }))}
            ajuda="A lista traz só o que o seu perfil pode conceder."
          />
        </div>

        {precisaOrg && (
          <Selecao
            rotulo="Organização"
            value={f.organizacaoId}
            onChange={(e) => setF({ ...f, organizacaoId: e.target.value })}
            opcoes={[
              { valor: '', texto: 'Selecione…' },
              ...organizacoes.map((o) => ({ valor: o.id, texto: nomeOrg(o) })),
            ]}
            ajuda="Este papel vale dentro de uma organização — é o que limita o que a pessoa enxerga."
          />
        )}

        <Aviso tom="info">
          A senha provisória é gerada automaticamente e mostrada uma única vez. Papéis que
          administram a plataforma passam a exigir MFA no primeiro acesso.
        </Aviso>
      </div>

      <div className="mt-5 flex justify-end gap-3">
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao disabled={!pronto} carregando={criar.isPending} onClick={() => criar.mutate()}>
          Criar usuário
        </Botao>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------

function FichaUsuario({
  id,
  aoFechar,
  podeConceder,
  organizacoes,
  meuId,
  aoErro,
  aoSucesso,
  aoGerarSenha,
}: {
  id: string
  aoFechar: () => void
  podeConceder: Papel[]
  organizacoes: Organizacao[]
  meuId: string
  aoErro: (e: string) => void
  aoSucesso: (m?: string) => void
  aoGerarSenha: (s: { nome: string; senha: string }) => void
}) {
  const qc = useQueryClient()
  const [novoPapel, setNovoPapel] = useState<Papel>(podeConceder[0] ?? 'operador')
  const [novaOrg, setNovaOrg] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['usuario', id],
    queryFn: () => api.get<Item<UsuarioDetalhe>>(`/api/v1/usuarios/${id}`),
  })

  const u = data?.dados
  const souEu = id === meuId

  function depois(mensagem: string) {
    void qc.invalidateQueries({ queryKey: ['usuario', id] })
    aoSucesso(mensagem)
  }

  const conceder = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/usuarios/${id}/papeis`, {
        papel: novoPapel,
        organizacaoId: (['admin', 'admin_master_egba', 'egba', 'leitura'] as Papel[]).includes(
          novoPapel,
        )
          ? null
          : novaOrg || null,
      }),
    onSuccess: () => depois('Papel concedido.'),
    onError: (e: Error) => aoErro(e.message),
  })

  const revogar = useMutation({
    mutationFn: (papelId: string) => api.delete(`/api/v1/usuarios/${id}/papeis/${papelId}`),
    onSuccess: () => depois('Papel revogado e sessões encerradas.'),
    onError: (e: Error) => aoErro(e.message),
  })

  const mudarVerticais = useMutation({
    mutationFn: (verticais: Array<'escolas' | 'companhias'>) =>
      api.patch(`/api/v1/usuarios/${id}`, { verticais }),
    onSuccess: () => depois('Recorte de verticais atualizado.'),
    onError: (e: Error) => aoErro(e.message),
  })

  const alternarAtivo = useMutation({
    mutationFn: (ativo: boolean) => api.patch(`/api/v1/usuarios/${id}`, { ativo }),
    onSuccess: (_r, ativo) => depois(ativo ? 'Usuário reativado.' : 'Usuário desativado.'),
    onError: (e: Error) => aoErro(e.message),
  })

  const redefinirSenha = useMutation({
    mutationFn: () =>
      api.post<Item<{ senhaProvisoria: string }>>(`/api/v1/usuarios/${id}/senha-provisoria`),
    onSuccess: (r) => {
      aoGerarSenha({ nome: u?.nome ?? '', senha: r.dados.senhaProvisoria })
      depois('Senha redefinida.')
    },
    onError: (e: Error) => aoErro(e.message),
  })

  const resetarMfa = useMutation({
    mutationFn: () => api.post(`/api/v1/usuarios/${id}/mfa/resetar`),
    onSuccess: () => depois('MFA removido. A pessoa cadastra o aplicativo no próximo acesso.'),
    onError: (e: Error) => aoErro(e.message),
  })

  const encerrarSessoes = useMutation({
    mutationFn: () => api.post(`/api/v1/usuarios/${id}/sessoes/encerrar`),
    onSuccess: () => depois('Sessões encerradas.'),
    onError: (e: Error) => aoErro(e.message),
  })

  const globalEscolhido = (['admin', 'admin_master_egba', 'egba', 'leitura'] as Papel[]).includes(
    novoPapel,
  )

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="xl"
      titulo={u?.nome ?? 'Usuário'}
      descricao={u?.email}
    >
      {isLoading || !u ? (
        <Carregando />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar usuarioId={u.id} nome={u.nome} temFoto={u.temFoto} tamanho={64} />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-600">
                {[u.cargo, u.setor].filter(Boolean).join(' · ') || 'Sem cargo informado'}
              </p>
              <p className="text-xs text-slate-400">
                {u.telefone || 'sem telefone'} · cadastrado em {formatarData(u.criadoEm)}
              </p>
            </div>
            {u.ativo ? <Etiqueta tom="sucesso">ativo</Etiqueta> : <Etiqueta tom="perigo">desativado</Etiqueta>}
          </div>

          {souEu && (
            <Aviso tom="info">
              Esta é a sua conta. Papéis, ativação e senha não se alteram aqui de propósito — é o
              que impede alguém de ampliar o próprio acesso ou se desligar sem volta. Use{' '}
              <strong>Meu perfil</strong> para seus dados e senha.
            </Aviso>
          )}

          {/* ----------------------------------------------------- verticais */}
          <section>
            <h3 className="mb-1 text-sm font-semibold text-slate-800">O que esta pessoa trabalha</h3>
            <p className="mb-2 text-xs text-slate-500">
              Define o recorte do painel: quem só atende companhias não vê o mercado de escolas, e
              vice-versa. Não muda permissão — muda o que aparece.
            </p>
            <div className="flex flex-wrap gap-4">
              {(
                [
                  ['escolas', 'Escolas (concluintes)'],
                  ['companhias', 'Companhias S.A. (publicações legais)'],
                ] as Array<['escolas' | 'companhias', string]>
              ).map(([chave, rotulo]) => {
                const atuais: Array<'escolas' | 'companhias'> =
                  Array.isArray(u.verticais) && u.verticais.length > 0
                    ? (u.verticais as Array<'escolas' | 'companhias'>)
                    : ['escolas', 'companhias']
                const marcado = atuais.includes(chave)
                return (
                  <label key={chave} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="size-4"
                      checked={marcado}
                      disabled={mudarVerticais.isPending || (marcado && atuais.length === 1)}
                      onChange={(e) =>
                        mudarVerticais.mutate(
                          e.target.checked
                            ? [...atuais, chave]
                            : atuais.filter((v) => v !== chave),
                        )
                      }
                    />
                    {rotulo}
                  </label>
                )
              })}
            </div>
          </section>

          {/* -------------------------------------------------------- papéis */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-800">Papéis</h3>
            {u.papeis.length === 0 ? (
              <Aviso tom="atencao">
                Sem papel nenhum: esta conta não consegue entrar. Conceda um abaixo.
              </Aviso>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-[color:var(--color-borda)]">
                {u.papeis.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-4 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">{ROTULO_PAPEL[p.papel]}</p>
                      <p className="truncate text-xs text-slate-500">
                        {p.organizacaoNome ?? 'Alcance global'} · desde {formatarData(p.criadoEm)}
                      </p>
                    </div>
                    <Botao
                      variante="secundario"
                      tamanho="sm"
                      disabled={souEu || !podeConceder.includes(p.papel)}
                      carregando={revogar.isPending}
                      onClick={() => revogar.mutate(p.id)}
                    >
                      Revogar
                    </Botao>
                  </li>
                ))}
              </ul>
            )}

            {!souEu && podeConceder.length > 0 && (
              <div className="mt-3 grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <Selecao
                  rotulo="Conceder papel"
                  value={novoPapel}
                  onChange={(e) => setNovoPapel(e.target.value as Papel)}
                  opcoes={podeConceder.map((p) => ({ valor: p, texto: ROTULO_PAPEL[p] }))}
                />
                <Selecao
                  rotulo="Organização"
                  value={globalEscolhido ? '' : novaOrg}
                  onChange={(e) => setNovaOrg(e.target.value)}
                  disabled={globalEscolhido}
                  opcoes={
                    globalEscolhido
                      ? [{ valor: '', texto: 'Papel global' }]
                      : [
                          { valor: '', texto: 'Selecione…' },
                          ...organizacoes.map((o) => ({ valor: o.id, texto: nomeOrg(o) })),
                        ]
                  }
                />
                <Botao
                  tamanho="sm"
                  disabled={!globalEscolhido && !novaOrg}
                  carregando={conceder.isPending}
                  onClick={() => conceder.mutate()}
                >
                  Conceder
                </Botao>
              </div>
            )}
          </section>

          {/* ------------------------------------------------------- sessões */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-800">
              Sessões abertas ({u.sessoesAtivas.length})
            </h3>
            {u.sessoesAtivas.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma sessão aberta agora.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-[color:var(--color-borda)] text-sm">
                {u.sessoesAtivas.map((s) => (
                  <li key={s.id} className="flex justify-between gap-4 px-3 py-2">
                    <span className="text-slate-700">{dispositivo(s.userAgent)}</span>
                    <span className="text-xs text-slate-400">
                      {s.ip ?? 'sem IP'} · desde {formatarData(s.criadoEm)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* -------------------------------------------------------- ações */}
          {!souEu && (
            <section className="border-t border-[color:var(--color-borda-suave)] pt-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-800">Ações de acesso</h3>
              <div className="flex flex-wrap gap-2">
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  carregando={redefinirSenha.isPending}
                  onClick={() => redefinirSenha.mutate()}
                >
                  Gerar nova senha
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={!u.mfaAtivo}
                  carregando={resetarMfa.isPending}
                  onClick={() => resetarMfa.mutate()}
                  title={u.mfaAtivo ? 'Para quem perdeu o celular' : 'MFA não está ativo'}
                >
                  Resetar MFA
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={u.sessoesAtivas.length === 0}
                  carregando={encerrarSessoes.isPending}
                  onClick={() => encerrarSessoes.mutate()}
                >
                  Encerrar sessões
                </Botao>
                <Botao
                  variante={u.ativo ? 'perigo' : 'primario'}
                  tamanho="sm"
                  carregando={alternarAtivo.isPending}
                  onClick={() => alternarAtivo.mutate(!u.ativo)}
                >
                  {u.ativo ? 'Desativar acesso' : 'Reativar acesso'}
                </Botao>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Contas são desativadas, nunca apagadas: o cadastro é referenciado por cada evento
                de auditoria e por cada pedido de publicação, e apagá-lo apagaria o registro de
                quem fez o quê.
              </p>
            </section>
          )}

          {/* ----------------------------------------------------- histórico */}
          <section className="border-t border-[color:var(--color-borda-suave)] pt-5">
            <h3 className="mb-2 text-sm font-semibold text-slate-800">Histórico recente</h3>
            {u.historico.length === 0 ? (
              <p className="text-sm text-slate-500">Nada registrado ainda.</p>
            ) : (
              <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
                {u.historico.map((h) => (
                  <li key={h.id} className="flex justify-between gap-3 py-1 text-slate-600">
                    <span>
                      <span className="font-medium text-slate-800">{h.acao}</span>{' '}
                      <span className="text-slate-400">
                        {h.souOAtor ? 'feito por esta conta' : 'feito nesta conta'} · {h.entidade}
                      </span>
                    </span>
                    <span className="shrink-0 text-slate-400">{formatarData(h.criadoEm)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <Botao variante="secundario" onClick={aoFechar}>
          Fechar
        </Botao>
      </div>
    </Modal>
  )
}
